import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';

export const StyledCardTableContainer = styled(Paper)({
  borderRadius: 8,
  boxShadow: 'none',
  background: '#fff',
  padding: 0,
  margin: 0,
  overflow: 'hidden',
  border: '1px solid #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  height: '100%',
  position: 'relative',
  '& .MuiTableHead-root': {
    background: '#eeeeee',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    overflow: 'hidden',
    boxShadow: 'inset 0 -1px 0 #e0e0e0',
  },
  '& .MuiTablePagination-root': {
    m: 0,
    p: 0,
    height: 41,
    background: '#eeeeee',
    borderTop: '1px solid #e0e0e0',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    '& .MuiTablePagination-toolbar': {
      minHeight: 40,
      padding: '0 12px',
      alignItems: 'center',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      fontSize: 11,
      color: '#444',
      fontWeight: 500,
    },
    '& .MuiTablePagination-select': {
      fontSize: 11,
      fontWeight: 500,
      color: '#3F49A7',
    },
    '& .MuiTablePagination-actions': {
      marginLeft: 12,
      '& .MuiIconButton-root': {
        padding: 8,
        color: '#3F49A7',
        '&:hover': {
          backgroundColor: '#eceef5',
        },
        '&.Mui-disabled': {
          color: '#bbb',
        },
      },
    },
  },
});

export const StyledTableContainer = styled(TableContainer)({
  background: '#fff',
  borderRadius: 0,
  boxShadow: 'none',
  border: 'none',
  width: '100%',
  padding: 0,
  margin: 0,
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
});

/** Scrollable area that fills the table container; scrollbars only when content overflows */
export const TableScrollWrapper = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflowX: 'auto',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    width: '10px',
    height: '12px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#e2e8f0',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#94a3b8',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#64748b',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#94a3b8 #e2e8f0',
});

export const StyledTable = styled(Table)({
  width: '100%',
  minWidth: 'max-content',
  borderCollapse: 'collapse',
  borderSpacing: 0,
  fontSize: 13,
  background: '#fff',
  padding: 0,
  margin: 0,
  borderRadius: 0,
  tableLayout: 'auto',
  '& tbody': {
    display: 'table-row-group',
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
});

export const StyledTableHead = styled(TableHead)({
  background: '#eeeeee',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  '& th': {
    fontWeight: 600,
    fontSize: 14,
    color: '#666666',
    background: '#eeeeee',
    borderBottom: '1px solid #e0e0e0',
    padding: '8px 10px',
    letterSpacing: 0.03,
    borderRadius: 0,
    height: 28,
    boxShadow: 'none',
    textShadow: 'none',
    // Fix: Ensure horizontal text
    writingMode: 'horizontal-tb',
    transform: 'none',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    fontSize: 12,
  },
});

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => !["zebra", "rowIdx"].includes(prop),
})(({ zebra, rowIdx }) => ({
  backgroundColor: zebra
    ? rowIdx % 2 === 1
      ? '#fafafa'
      : '#fff'
    : '#fff',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f8f9ff',
  },
  borderBottom: '1px solid #e0e0e0',
  height: 32,
  '&:last-child': {
    '& td': {
      borderBottom: 'none',
    },
    '&:hover td:first-of-type': {
      borderBottomLeftRadius: 8,
    },
    '&:hover td:last-of-type': {
      borderBottomRightRadius: 8,
    },
  },
}));

export const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => !["header", "sorted"].includes(prop),
})(({ header, sorted }) => ({
  fontWeight: header ? 600 : 400,
  fontSize: header ? 12 : 12,
  color: header ? '#666666' : '#232323',
  borderBottom: '1px solid #e0e0e0',
  background: header ? '#eeeeee' : '#fff',
  ...(header && {
    background: '#eeeeee !important',
  }),
  padding: header ? '6px 10px' : '4px 10px',
  verticalAlign: 'middle',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  height: 32,
  maxHeight: 32,
  transition: 'all 0.2s ease',
  ...(header && sorted && {
    borderBottom: '2px solid #666666',
    background: '#eeeeee',
    color: '#666666',
    fontWeight: 700,
  }),
  ...(header && {
    cursor: 'pointer',
    '&:first-of-type': {
      borderTopLeftRadius: 8,
    },
    '&:last-of-type': {
      borderTopRightRadius: 8,
    },
  }),
})); 